import { useEffect, useState, type FormEvent } from "react";
import { useOutletContext } from "react-router";
import { api } from "@/lib/api";
import type { AdminContext } from "@/components/admin/AdminShell";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SettingsStatus = {
  stripe: {
    secretKeySet: boolean;
    webhookSecretSet: boolean;
    monthlyPriceId: string | null;
    annualPriceId: string | null;
  };
  discord: {
    botTokenSet: boolean;
    guildId: string | null;
    memberRoleId: string | null;
    inviteUrl: string | null;
  };
  klaviyo: {
    apiKeySet: boolean;
    listId: string | null;
  };
  features: { shop: boolean; guides: boolean };
};

type TestResult = { ok: boolean; message: string };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function SecretInput({
  label,
  alreadySet,
  value,
  onChange,
  hint,
}: {
  label: string;
  alreadySet: boolean;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label className="fld">
      <span>
        {label}
        {alreadySet && !value && (
          <span className="badge-set">set</span>
        )}
      </span>
      <input
        type="password"
        autoComplete="new-password"
        className="inp"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={alreadySet ? "(stored — leave blank to keep)" : "paste secret value"}
      />
      {hint && <small className="hint">{hint}</small>}
    </label>
  );
}

function PlainInput({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <label className="fld">
      <span>{label}</span>
      <input
        type="text"
        className="inp"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint && <small className="hint">{hint}</small>}
    </label>
  );
}

function TestBanner({ result }: { result: TestResult | null }) {
  if (!result) return null;
  return (
    <div className={result.ok ? "test-banner ok" : "test-banner fail"}>
      {result.ok ? "✓ " : "✗ "}
      {result.message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AdminSettings() {
  const { role } = useOutletContext<AdminContext>();
  const isAdmin = role === "admin";
  const [status, setStatus] = useState<SettingsStatus | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Stripe form state
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState("");
  const [stripeMonthlyPriceId, setStripeMonthlyPriceId] = useState("");
  const [stripeAnnualPriceId, setStripeAnnualPriceId] = useState("");
  const [stripeSaving, setStripeSaving] = useState(false);
  const [stripeErr, setStripeErr] = useState<string | null>(null);
  const [stripeTest, setStripeTest] = useState<TestResult | null>(null);
  const [stripeTesting, setStripeTesting] = useState(false);

  // Discord form state
  const [discordBotToken, setDiscordBotToken] = useState("");
  const [discordGuildId, setDiscordGuildId] = useState("");
  const [discordMemberRoleId, setDiscordMemberRoleId] = useState("");
  const [discordInviteUrl, setDiscordInviteUrl] = useState("");
  const [discordSaving, setDiscordSaving] = useState(false);
  const [discordErr, setDiscordErr] = useState<string | null>(null);
  const [discordTest, setDiscordTest] = useState<TestResult | null>(null);
  const [discordTesting, setDiscordTesting] = useState(false);

  // Klaviyo form state
  const [klaviyoApiKey, setKlaviyoApiKey] = useState("");
  const [klaviyoListId, setKlaviyoListId] = useState("");
  const [klaviyoSaving, setKlaviyoSaving] = useState(false);
  const [klaviyoErr, setKlaviyoErr] = useState<string | null>(null);
  const [klaviyoTest, setKlaviyoTest] = useState<TestResult | null>(null);
  const [klaviyoTesting, setKlaviyoTesting] = useState(false);

  const [featuresSaving, setFeaturesSaving] = useState<string | null>(null);

  async function toggleFeature(flag: "shop" | "guides", value: boolean) {
    setFeaturesSaving(flag);
    try {
      await api.post("/api/admin/settings/features", { [flag]: value });
      await loadStatus();
    } finally {
      setFeaturesSaving(null);
    }
  }

  async function loadStatus() {
    const s = await api.get<SettingsStatus>("/api/admin/settings");
    setStatus(s);
    // Populate non-secret fields from current status
    setStripeMonthlyPriceId(s.stripe.monthlyPriceId ?? "");
    setStripeAnnualPriceId(s.stripe.annualPriceId ?? "");
    setDiscordGuildId(s.discord.guildId ?? "");
    setDiscordMemberRoleId(s.discord.memberRoleId ?? "");
    setDiscordInviteUrl(s.discord.inviteUrl ?? "");
    setKlaviyoListId(s.klaviyo.listId ?? "");
  }

  useEffect(() => {
    loadStatus().finally(() => setLoaded(true));
  }, []);

  // ---------------------------------------------------------------------------
  // Save handlers
  // ---------------------------------------------------------------------------

  async function saveStripe(e: FormEvent) {
    e.preventDefault();
    setStripeSaving(true);
    setStripeErr(null);
    try {
      await api.post("/api/admin/settings/stripe", {
        secretKey: stripeSecretKey,
        webhookSecret: stripeWebhookSecret,
        monthlyPriceId: stripeMonthlyPriceId || null,
        annualPriceId: stripeAnnualPriceId || null,
      });
      setStripeSecretKey("");
      setStripeWebhookSecret("");
      await loadStatus();
    } catch (err) {
      setStripeErr(err instanceof Error ? err.message : "Save failed");
    } finally {
      setStripeSaving(false);
    }
  }

  async function testStripe() {
    setStripeTesting(true);
    setStripeTest(null);
    try {
      const r = await api.post<TestResult>("/api/admin/settings/stripe/test", {});
      setStripeTest(r);
    } catch (err) {
      setStripeTest({ ok: false, message: err instanceof Error ? err.message : "Test failed" });
    } finally {
      setStripeTesting(false);
    }
  }

  async function saveDiscord(e: FormEvent) {
    e.preventDefault();
    setDiscordSaving(true);
    setDiscordErr(null);
    try {
      await api.post("/api/admin/settings/discord", {
        botToken: discordBotToken,
        guildId: discordGuildId || null,
        memberRoleId: discordMemberRoleId || null,
        inviteUrl: discordInviteUrl || null,
      });
      setDiscordBotToken("");
      await loadStatus();
    } catch (err) {
      setDiscordErr(err instanceof Error ? err.message : "Save failed");
    } finally {
      setDiscordSaving(false);
    }
  }

  async function testDiscord() {
    setDiscordTesting(true);
    setDiscordTest(null);
    try {
      const r = await api.post<TestResult>("/api/admin/settings/discord/test", {});
      setDiscordTest(r);
    } catch (err) {
      setDiscordTest({ ok: false, message: err instanceof Error ? err.message : "Test failed" });
    } finally {
      setDiscordTesting(false);
    }
  }

  async function saveKlaviyo(e: FormEvent) {
    e.preventDefault();
    setKlaviyoSaving(true);
    setKlaviyoErr(null);
    try {
      await api.post("/api/admin/settings/klaviyo", {
        apiKey: klaviyoApiKey,
        listId: klaviyoListId || null,
      });
      setKlaviyoApiKey("");
      await loadStatus();
    } catch (err) {
      setKlaviyoErr(err instanceof Error ? err.message : "Save failed");
    } finally {
      setKlaviyoSaving(false);
    }
  }

  async function testKlaviyo() {
    setKlaviyoTesting(true);
    setKlaviyoTest(null);
    try {
      const r = await api.post<TestResult>("/api/admin/settings/klaviyo/test", {});
      setKlaviyoTest(r);
    } catch (err) {
      setKlaviyoTest({ ok: false, message: err instanceof Error ? err.message : "Test failed" });
    } finally {
      setKlaviyoTesting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!loaded) {
    return (
      <section>
        <style>{css}</style>
        <h1 className="aflt-admin-title">Settings</h1>
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      </section>
    );
  }

  return (
    <section>
      <style>{css}</style>
      <h1 className="aflt-admin-title">Settings</h1>
      <p className="intro">
        {isAdmin
          ? "Integration credentials. Secrets are encrypted at rest. Leave a secret field blank to keep the stored value. Set a field here to override its env-var fallback."
          : "Toggle which public pages are live. Integration credentials (Stripe, Klaviyo, Discord) are managed by admins."}
      </p>

      {/* ── Site sections (feature flags) ──────────────── */}
      <div className="paper-card">
        <legend className="form-legend">Site sections</legend>
        <p className="note" style={{ marginTop: 0 }}>
          Toggle which public pages are live. When off, the page redirects home and
          its nav/footer links are hidden.
        </p>
        {([
          { flag: "shop", label: "Shop", desc: "Storefront + product pages + cart" },
          { flag: "guides", label: "Guides", desc: "Guides index, topics, and detail pages" },
        ] as const).map((f) => (
          <label key={f.flag} className="feature-row">
            <input
              type="checkbox"
              checked={status?.features[f.flag] ?? false}
              disabled={featuresSaving === f.flag}
              onChange={(e) => toggleFeature(f.flag, e.target.checked)}
            />
            <span>
              <strong>{f.label}</strong>
              <span className="feature-desc">{f.desc}</span>
            </span>
            <span className="feature-state">
              {featuresSaving === f.flag
                ? "Saving…"
                : status?.features[f.flag]
                  ? "Live"
                  : "Hidden"}
            </span>
          </label>
        ))}
      </div>

      {isAdmin && (
      <>
      {/* ── Stripe ─────────────────────────────────────── */}
      <form onSubmit={saveStripe} className="paper-card">
        <legend className="form-legend">Stripe</legend>
        <TestBanner result={stripeTest} />
        <SecretInput
          label="Secret key"
          alreadySet={status?.stripe.secretKeySet ?? false}
          value={stripeSecretKey}
          onChange={setStripeSecretKey}
          hint="sk_live_… or sk_test_…"
        />
        <SecretInput
          label="Webhook signing secret"
          alreadySet={status?.stripe.webhookSecretSet ?? false}
          value={stripeWebhookSecret}
          onChange={setStripeWebhookSecret}
          hint="whsec_… (Stripe dashboard → Webhooks → endpoint → signing secret)"
        />
        <PlainInput
          label="Membership price ID — monthly"
          value={stripeMonthlyPriceId}
          onChange={setStripeMonthlyPriceId}
          placeholder="price_…"
        />
        <PlainInput
          label="Membership price ID — annual"
          value={stripeAnnualPriceId}
          onChange={setStripeAnnualPriceId}
          placeholder="price_…"
        />
        {stripeErr && <p className="err">{stripeErr}</p>}
        <div className="actions">
          <button type="submit" className="btn-woven" disabled={stripeSaving}>
            {stripeSaving ? "Saving…" : "Save Stripe"}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={testStripe}
            disabled={stripeTesting}
          >
            {stripeTesting ? "Testing…" : "Test connection"}
          </button>
        </div>
      </form>

      {/* ── Discord ────────────────────────────────────── */}
      <form onSubmit={saveDiscord} className="paper-card" style={{ marginTop: "1.5rem" }}>
        <legend className="form-legend">Discord</legend>
        <TestBanner result={discordTest} />
        <SecretInput
          label="Bot token"
          alreadySet={status?.discord.botTokenSet ?? false}
          value={discordBotToken}
          onChange={setDiscordBotToken}
          hint="Discord developer portal → Bot → Token"
        />
        <PlainInput
          label="Guild ID"
          value={discordGuildId}
          onChange={setDiscordGuildId}
          placeholder="numeric server ID"
        />
        <PlainInput
          label="Member role ID"
          value={discordMemberRoleId}
          onChange={setDiscordMemberRoleId}
          placeholder="numeric role ID granted on active subscription"
        />
        <PlainInput
          label="Invite URL"
          value={discordInviteUrl}
          onChange={setDiscordInviteUrl}
          placeholder="https://discord.gg/…"
          hint="Shown to subscribed members on the account page"
        />
        {discordErr && <p className="err">{discordErr}</p>}
        <div className="actions">
          <button type="submit" className="btn-woven" disabled={discordSaving}>
            {discordSaving ? "Saving…" : "Save Discord"}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={testDiscord}
            disabled={discordTesting}
          >
            {discordTesting ? "Testing…" : "Test connection"}
          </button>
        </div>
      </form>

      {/* ── Klaviyo ────────────────────────────────────── */}
      <form onSubmit={saveKlaviyo} className="paper-card" style={{ marginTop: "1.5rem" }}>
        <legend className="form-legend">Klaviyo (newsletter)</legend>
        <TestBanner result={klaviyoTest} />
        <SecretInput
          label="API key"
          alreadySet={status?.klaviyo.apiKeySet ?? false}
          value={klaviyoApiKey}
          onChange={setKlaviyoApiKey}
          hint="pk_… (Klaviyo → Account → Settings → API Keys)"
        />
        <PlainInput
          label="Default list ID"
          value={klaviyoListId}
          onChange={setKlaviyoListId}
          placeholder="6-char list ID"
        />
        {klaviyoErr && <p className="err">{klaviyoErr}</p>}
        <div className="actions">
          <button type="submit" className="btn-woven" disabled={klaviyoSaving}>
            {klaviyoSaving ? "Saving…" : "Save Klaviyo"}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={testKlaviyo}
            disabled={klaviyoTesting}
          >
            {klaviyoTesting ? "Testing…" : "Test connection"}
          </button>
        </div>
      </form>
      </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// CSS
// ---------------------------------------------------------------------------

const css = `
  .intro {
    font-family: ${fonts.body}; font-size: 0.875rem; color: rgba(8,45,61,0.65);
    max-width: 48rem; line-height: 1.55; margin-bottom: 1.5rem;
  }
  .feature-row {
    display: flex; align-items: center; gap: 0.75rem; padding: 0.7rem 0;
    border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body};
  }
  .feature-row:last-child { border-bottom: none; }
  .feature-row input[type=checkbox] { width: 18px; height: 18px; accent-color: ${colors.berryRose}; cursor: pointer; }
  .feature-row strong { display: block; font-size: 14px; color: ${colors.navy}; }
  .feature-desc { display: block; font-size: 12px; color: rgba(8,45,61,0.55); }
  .feature-row > span:first-of-type { flex: 1; }
  .feature-state {
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em;
    color: rgba(8,45,61,0.55);
  }
  .paper-card {
    background: #fff; border: 1.5px dashed rgba(201,147,76,0.40);
    border-radius: 16px; padding: 1.5rem 1.75rem;
  }
  .form-legend {
    font-family: ${fonts.logo}; font-weight: 500; font-size: 1.2rem;
    color: ${colors.navy}; display: block; margin-bottom: 1.25rem;
  }
  .fld { display: block; margin-bottom: 1rem; }
  .fld > span {
    display: flex; align-items: center; gap: 0.4rem;
    font-family: ${fonts.body}; font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.1em; color: rgba(8,45,61,0.65);
    margin-bottom: 0.35rem;
  }
  .badge-set {
    background: #606b4c; color: #fff8ed;
    padding: 0.1rem 0.45rem; border-radius: 999px;
    font-size: 0.6rem; text-transform: uppercase; letter-spacing: 0.1em; font-weight: 700;
  }
  .inp {
    width: 100%; padding: 0.55rem 0.9rem;
    border: 1px solid rgba(201,147,76,0.40); border-radius: 999px;
    background: ${colors.cream}; color: ${colors.navy};
    font-family: ${fonts.body}; font-weight: 600; font-size: 13.5px; outline: none;
    box-sizing: border-box;
  }
  .inp:focus { border-color: ${colors.berryRose}; box-shadow: 0 4px 14px -8px rgba(212,96,106,0.55); }
  .hint {
    display: block; margin-top: 0.3rem;
    font-family: ${fonts.body}; font-size: 11px; color: rgba(8,45,61,0.5);
  }
  .test-banner {
    padding: 0.6rem 0.9rem; border-radius: 0.5rem;
    margin-bottom: 0.75rem; font-weight: 600;
    font-family: ${fonts.body}; font-size: 0.875rem;
  }
  .test-banner.ok  { background: #606b4c; color: #fff8ed; }
  .test-banner.fail { background: ${colors.berryRose}; color: #fff8ed; }
  .actions { display: flex; gap: 0.6rem; margin-top: 0.5rem; flex-wrap: wrap; }
  .btn-woven {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    background: ${colors.berryRose}; color: #fff; font-family: ${fonts.body};
    font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    border: none; border-radius: 7px; cursor: pointer;
    outline: 2px dashed rgba(255,255,255,0.45); outline-offset: -5px;
    box-shadow: 0 2px 8px rgba(212,96,106,0.30);
  }
  .btn-woven:hover { background: #c0535d; }
  .btn-woven:disabled { opacity: 0.6; cursor: default; }
  .btn-ghost {
    background: ${colors.cream}; border: 1px solid rgba(201,147,76,0.55); color: ${colors.navy};
    font-family: ${fonts.body}; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em;
    text-transform: uppercase; border-radius: 7px; padding: 9px 16px; cursor: pointer;
  }
  .btn-ghost:hover { box-shadow: 0 6px 14px -8px rgba(8,45,61,0.35); }
  .btn-ghost:disabled { opacity: 0.6; cursor: default; }
  .err {
    color: ${colors.berryRose}; font-family: ${fonts.body};
    font-weight: 600; font-size: 0.85rem; margin: 0.5rem 0;
  }
`;
